import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getKingdomPolitics } from '../api/politics';
import type { PoliticsStandingsResponse } from '../types/politics';

// Module-level TTL cache: shared between Sidebar and Politics page.
interface CacheEntry {
  data: PoliticsStandingsResponse;
  expiresAt: number;
}
const cache = new Map<string, CacheEntry>();
const CACHE_TTL_MS = 30_000; // 30 seconds

export function _resetKingdomPoliticsCache() {
  cache.clear();
}

interface UseKingdomPoliticsResult {
  data: PoliticsStandingsResponse | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useKingdomPolitics(kingdomId: string | null): UseKingdomPoliticsResult {
  const { worldId } = useAuth();
  const [data, setData] = useState<PoliticsStandingsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPolitics = useCallback(async (forceRefresh = false) => {
    if (!kingdomId || !worldId) {
      setData(null);
      return;
    }

    const cacheKey = `${worldId}:${kingdomId}`;
    const now = Date.now();
    const cached = cache.get(cacheKey);

    if (!forceRefresh && cached && cached.expiresAt > now) {
      setData(cached.data);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await getKingdomPolitics(worldId, kingdomId);
      cache.set(cacheKey, { data: result, expiresAt: now + CACHE_TTL_MS });
      setData(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load politics');
    } finally {
      setIsLoading(false);
    }
  }, [kingdomId, worldId]);

  const refresh = useCallback(() => {
    fetchPolitics(true);
  }, [fetchPolitics]);

  useEffect(() => {
    fetchPolitics(false);
  }, [fetchPolitics]);

  return { data, isLoading, error, refresh };
}
